// Playback: moving the playhead through the event stream.
//
// Deliberately dependency-free apart from the workflow rule, which is pure
// string logic. Keep it that way: this module is loaded by the render loop's
// callers and there is no reason for it to know about the scene.

import { isRegression } from './workflow';
//
// Kept pure and out of the component for the usual reason — this project
// tests pure functions and has no DOM test environment — and for a
// specific one: the arithmetic here decides how fast the campaign plays,
// and an off-by-one makes it skip events, which is invisible. You would
// simply never see some of what happened.
//
// THE PLAYHEAD MOVES THROUGH EVENTS, NOT THROUGH TIME
//
// Advancing at a fixed number of MILLISECONDS per second would make the
// replay's speed depend on how busy the team was: a quiet overnight
// stretch would crawl past at the same pixels-per-second as a busy
// morning, and a busy morning would flash by. Measured on this data, the
// working day holds 47-102 events an hour and the rest holds 1-13, so the
// two are not comparable.
//
// Advancing at a fixed number of EVENTS per second means every step is
// the same amount of story. That is what "blow by blow" needs, and it is
// also the only rate the operator can reason about: "4 events a second"
// describes what they will see, "6 hours a second" does not.

/** Events per second. Slow enough to follow, fast enough to reach the end
 *  of a campaign in a sitting. */
export const PLAYBACK_SPEEDS = [1, 2, 4, 8, 16] as const;
export const DEFAULT_SPEED = 4;

export interface PlaybackFrame {
  /** Index into the instants array after this frame. */
  index: number;
  /** Fraction of an event carried into the next frame. Without it, a
   *  fractional rate would never advance at all: at 1 event/second a
   *  60fps render loop moves 0.0167 events per frame and would round to
   *  zero forever. */
  carry: number;
  /** True once the playhead is at the last event. */
  done: boolean;
}

/**
 * Advance the playhead by `ratePerSec` events per second, for `dtMs`.
 *
 * Fractional progress is carried rather than dropped, and the result is
 * clamped at the end of the stream rather than wrapping or overshooting.
 */
export function advancePlayback(
  eventCount: number,
  fromIndex: number,
  ratePerSec: number,
  dtMs: number,
  carry = 0,
): PlaybackFrame {
  const last = Math.max(0, eventCount - 1);
  if (eventCount <= 0) return { index: 0, carry: 0, done: true };

  // A long frame — a backgrounded tab, a slow first paint — must not skip
  // the whole campaign. Capped at a quarter second of playback.
  const dt = Math.min(Math.max(dtMs, 0), 250);
  const rate = Math.max(0, ratePerSec);

  // Accumulated in MILLIONTHS of an event, as integers.
  //
  // Floating-point accumulation drifts, and it drifts in the direction
  // that loses steps: at 2 events/second and 60fps the sum lands on
  // 0.9999999999999999 once every thirty frames, floors to zero, and the
  // whole replay then runs one event behind for its entire length. Real,
  // measured, and invisible - you would just never see the last thing
  // that happened.
  //
  // Rounding the per-frame DELTA keeps the error bounded at half a
  // micro-event per frame instead of accumulating into the index.
  const movedMicro = carry * 1e6 + rate * dt * 1000;

  // The epsilon absorbs binary representation error, and it is not a
  // fudge: it promotes a value that is a billionth of an event short of
  // whole to whole, and a billionth of an event is not a quantity anyone
  // can observe. Without it, `2 x (1000/60) / 1000` summed thirty times
  // lands on 0.9999999999999999, floors to zero, and the replay runs a
  // step behind for its whole length.
  const whole = Math.floor(movedMicro / 1e6 + 1e-9);
  const next = Math.min(last, fromIndex + whole);
  const done = next >= last;

  return {
    index: next,
    carry: done ? 0 : (movedMicro - whole * 1e6) / 1e6,
    done,
  };
}

/** How many events have happened at or before `ms`. This is the playhead's
 *  position expressed as an event index, and it is what the rail's "event
 *  N of M" readout shows. */
export function indexAt(instants: number[], ms: number): number {
  let lo = 0;
  let hi = instants.length;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (instants[mid]! <= ms) lo = mid + 1;
    else hi = mid;
  }
  return lo;
}

/** The playhead's instant for an event index. Out-of-range indices clamp,
 *  so a stale index after a shorter timeline arrives cannot produce
 *  undefined. */
export function instantAtIndex(instants: number[], index: number, fallbackMs: number): number {
  if (instants.length === 0) return fallbackMs;
  const i = Math.max(0, Math.min(instants.length - 1, index));
  return instants[i]!;
}

/** Something worth a mark on the board, and what KIND of thing it is.
 *
 *  Three kinds, three indicators, because they are three different things
 *  to look at: work MOVED, a CONVERSATION happened, or a PR moved. Drawing
 *  them all as the same circle says only "something happened here", which
 *  is the one thing the operator already knows. */
export type PingKind = 'transition' | 'comment' | 'pr';

export interface Ping {
  key: string;
  ms: number;
  kind: PingKind;
  /** Who caused it, resolved to a platform user id. Null when the actor is
   *  unmapped — the event still happened, it just has nobody to attribute
   *  it to, and a ray with no origin is not worth drawing. */
  actor?: string | null;
  /** Who is ASSIGNED to it as of this event. Used only for a regression,
   *  which is aimed at the person accountable rather than at whoever did
   *  it. */
  assignee?: string | null;
  /**
   * A transition that moved the ticket BACKWARDS through the workflow.
   *
   * A flag on a transition rather than a fourth kind, because it is not a
   * different sort of thing happening — it is the same thing pointing the
   * wrong way. The board draws it red and aims it at the assignee. See
   * features/battlemap/workflow.ts for the rule.
   */
  backwards?: boolean;
  /**
   * The FIRST time this ticket was ever moved, on work that was never
   * committed — so the moment a new challenge appeared in the field.
   *
   * Shares the red-and-exclamation treatment with a regression, and that is
   * deliberate rather than lazy: both mean "bad news, look here". One
   * channel, one meaning — an interruption to the plan, whether it arrived
   * as new work or as work going backwards.
   */
  arrived?: boolean;
}

/** Whether a ping should be drawn as an alarm: red, aimed at the person
 *  accountable, with an exclamation.
 *
 *  Named rather than repeated, because a third reason to alarm should be a
 *  deliberate addition to this list and not a fourth `||` in a JSX
 *  expression somewhere. */
export function isAlarm(p: { backwards?: boolean; arrived?: boolean }): boolean {
  return p.backwards === true || p.arrived === true;
}

/**
 * The pingable events, ascending.
 *
 * `created` is deliberately absent. A creation is not yet anything: the
 * ticket is queued until somebody moves it, and the board says so. Marking
 * creations would put a ring on a piece that is not there.
 *
 * Computed once per timeline rather than per frame — there are ~800 events
 * in a live window, and re-walking the stream every frame would be the
 * expensive part of a 60fps loop.
 */
export function pingStream(
  events: Array<{
    t: string; key: string; kind: string;
    actor?: string | null; assignee?: string | null;
    from?: string; to?: string;
  }>,
  /** Keys that ARRIVED during this window: work that was never committed
   *  and got moved. The same set the board draws as orange reinforcements,
   *  so the exclamation marks exactly the pieces that appeared unplanned
   *  and nothing else. */
  arrivals: Iterable<string> = [],
): Ping[] {
  const arrivalSet = new Set(arrivals);
  // First transition seen per key, in one pass: the events are ascending, so
  // the first one encountered IS the first one.
  const moved = new Set<string>();
  const out: Ping[] = [];
  for (const e of events) {
    if (e.kind !== 'transition' && e.kind !== 'comment' && e.kind !== 'pr') continue;
    const ms = Date.parse(e.t);
    if (Number.isNaN(ms)) continue;
    const p: Ping = { key: e.key, ms, kind: e.kind, actor: e.actor ?? null };
    if (e.assignee) p.assignee = e.assignee;
    if (e.kind === 'transition') {
      if (isRegression(e.from, e.to)) p.backwards = true;
      // Only the FIRST move counts. A reinforcement's second transition is
      // not another arrival — it is the same challenge being worked.
      if (!moved.has(e.key)) {
        moved.add(e.key);
        if (arrivalSet.has(e.key)) p.arrived = true;
      }
    }
    out.push(p);
  }
  out.sort((a, b) => a.ms - b.ms);
  return out;
}

/**
 * The events the playhead crossed between two instants.
 *
 * Half-open on the LEFT — (fromMs, toMs] — because consecutive frames share
 * an endpoint. Closing both ends would fire the boundary event twice on
 * every step, which at 16 events a second is a visible stutter of doubled
 * marks.
 *
 * THE STANDING RULE DIFFERS BY KIND, and that is not an inconsistency:
 *
 *   transitions  always fire. The piece IS the thing changing, and a move
 *                that closes a ticket has to be visible as it happens —
 *                the fall is the point. Suppressing it because the piece
 *                has "already closed" would delete every ending.
 *
 *   comments and PRs  fire only on tickets still standing at that instant.
 *                They are a beat laid ON a piece, and a beat on work that
 *                has left the board has nothing to land on. The operator
 *                chose this for comments and it applies to PRs for the
 *                same reason.
 */
export function pingsBetween(
  stream: Ping[],
  fromMs: number,
  toMs: number,
  closedAtMs: Map<string, number | null>,
): Ping[] {
  const out: Ping[] = [];
  for (const p of stream) {
    if (p.ms <= fromMs || p.ms > toMs) continue;
    if (p.kind !== 'transition') {
      const closed = closedAtMs.get(p.key) ?? null;
      if (closed !== null && closed <= p.ms) continue;
    }
    out.push(p);
  }
  return out;
}



/**
 * Track when each ticket closed, from a terminal-status vocabulary.
 *
 * Separate from commentStream because it needs the team's status names,
 * and separate from the replay fold because the fold answers "what is true
 * AT an instant" while this answers "when did it become true", which is
 * what a ping needs.
 */
export function closingInstants(
  events: Array<{ t: string; key: string; kind: string; to?: string }>,
  terminalStatuses: string[],
): Map<string, number | null> {
  const closed = new Map<string, number | null>();
  for (const e of events) {
    if (e.kind !== 'transition' || !e.to) continue;
    const ms = Date.parse(e.t);
    if (Number.isNaN(ms)) continue;
    if (terminalStatuses.includes(e.to)) closed.set(e.key, ms);
    // A move back out of a terminal status reopens it, and a ping on
    // reopened work should resume.
    else closed.delete(e.key);
  }
  return closed;
}
